import React, { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CustomerSection from './CustomerSection';
import ChevronArrowRightReactIcon from 'assets/react-icons/ChevronArrowRightReactIcon';
import LineItemsSection from './LineItemsSection';
import classNames from 'classnames';
import { ENTITY_TYPE, PopoverArrowTypes } from 'constants/common';
import { twMerge } from 'tailwind-merge';
import DateUtils from 'utils/dateUtils';
import { getJobStatusColor, getJobStatusText } from '../utils';
import MoreMenu from 'containers/receipts/components/MoreMenu';
import { WebAppColors } from 'constants/colors';
import { LoadingSpinnerAnimation } from 'assets/animations';
import {
  AddIconWoPadding,
  AddNoteIcon,
  ArchiveJobIcon,
  ArrowRightIcon,
  CalendarIcon,
  CaretDownIcon,
  CustomersTabIcon,
  DeleteIcon,
  EditIcon,
  EditIconBlue,
  NavCloseIcon,
  WhiteInfoIcon
} from 'assets/icons';
import { CustomPopover, GenericDeleteModal, GenericError, If } from 'components';
import Lottie from 'lottie-react';
import { VoidFn } from 'types/baseTypes';
import { Job, JobTimelineEvent, RelatedInvoiceForJob } from '../jobs.model';
import JobTimelineSection from './JobTimelineSection';
import routesPath from 'routes/RoutesPath';
import { useUnlinkJobAndInvoiceMutation } from '../api';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectCurrentTeam, selectCurrentTeamTeamSettingsPermissions, selectIsOwner } from 'containers/app/appSlice';
import { InvoiceStatus } from 'containers/invoices/invoices.model';
import { FROM_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import { JobStatus } from '../constant';
import { logAnalyticEvent } from 'utils/analytics';
import { CleverTapEventsInvoices } from 'containers/invoices/events';
import RelatedEstimateCard from 'containers/estimates/components/RelatedEstimateCard';
import { useDisconnectJobOrInvoiceFromEstimateMutation } from 'containers/estimates/api';
import RelatedInvoiceCard from 'containers/invoices/components/RelatedInvoiceCard';
import { InvoiceDetails } from 'containers/estimates/estimates.model';
import { resetEstimateState } from 'containers/estimates/estimatesSlice';
import { SubscriptionStatus } from 'containers/app/app.model';
import { UnlinkSourceType } from 'containers/estimates/constants';

interface JobDetailsPanelProps {
  job: Job;
  onEditAssigneesClick: VoidFn;
  onEditStatusClick: VoidFn;
  onEditDescriptionClick: VoidFn;
  onEditLeadSourceClick: VoidFn;
  onEditScheduleClick: VoidFn;
  getTeamMemberNames: () => JSX.Element | string;
  isRootState: boolean;
  handleSidePanelClose: VoidFn;
  handleCustomerClick: VoidFn;
  isJobByIdFetching: boolean;
  jobTimeline: JobTimelineEvent[];
  isJobTimelineFetching: boolean;
  onViewHistoryClick: VoidFn;
  onAddNoteClick: VoidFn;
  onEditNoteClick: (event: JobTimelineEvent) => void;
  onDeleteNote: (event: JobTimelineEvent) => void;
  refetchJob: (id: string) => void;
  handleInvoiceClick: (invoiceId: string) => void;
  handleStatusChange: (jobStatus: JobStatus) => void;
  handleDeleteJob: VoidFn;
  isSingleMemberTeam: boolean;
  isError: boolean;
  refetchEstimatesList: VoidFn;
}

const JobDetailsPanel: FC<JobDetailsPanelProps> = ({
  onEditAssigneesClick,
  onEditStatusClick,
  getTeamMemberNames,
  isRootState,
  handleSidePanelClose,
  job,
  handleCustomerClick,
  onEditDescriptionClick,
  isJobByIdFetching,
  onEditLeadSourceClick,
  onEditScheduleClick,
  jobTimeline,
  isJobTimelineFetching,
  onViewHistoryClick,
  onAddNoteClick,
  onEditNoteClick,
  onDeleteNote,
  refetchJob,
  handleInvoiceClick,
  handleStatusChange,
  handleDeleteJob,
  isSingleMemberTeam,
  isError,
  refetchEstimatesList
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const manageTeamPermissions = useAppSelector(selectCurrentTeamTeamSettingsPermissions);
  const isOwner = useAppSelector(selectIsOwner);
  const [currentNotesActionsPopoverId, setCurrentNotesActionsPopoverId] = useState(null);

  const { rootJobId, rootReceiptId, rootCustomerId, rootInvoiceId, rootDisputeId, rootEstimateId, rootPage } =
    useParams();

  const [unlinkJobAndInvoiceMutation, { isLoading: isUnlinkInProgress, isSuccess: unlinkSuccess }] =
    useUnlinkJobAndInvoiceMutation();

  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<{
    id: string;
    isOpen: boolean;
    type: ENTITY_TYPE | null;
  }>({
    id: '',
    isOpen: false,
    type: null
  });

  const [disconnectJobOrInvoiceFromEstimate, { isLoading: isDisconnecting }] =
    useDisconnectJobOrInvoiceFromEstimateMutation();

  useEffect(() => {
    if (unlinkSuccess) {
      handleCloseConfirmationModal();
      refetchJob(job.id);
    }
  }, [unlinkSuccess, isUnlinkInProgress]);

  const bottomBar = [
    {
      id: 'job-details-bottom-bar-add-note-button',
      isDisabled: false,
      icon: (
        <div className="path-fill-current path">
          <AddNoteIcon className="shrink-0" />
        </div>
      ),
      disabledReason: '',
      btnText: 'Add note',
      onClickHandler: onAddNoteClick
    },
    {
      id: 'job-details-bottom-bar-edit-button',
      icon: (
        <div>
          <EditIcon className="h-5 shrink-0" />
        </div>
      ),
      isDisabled: false,
      disabledReason: '',
      btnText: 'Edit',
      onClickHandler: () => {
        navigate(routesPath.EDIT_JOB.replace(':jobId', job.id));
      }
    }
  ];

  const moreMenuItems = [
    {
      id: 'more-button-archive-job',
      icon: <ArchiveJobIcon className="w-5 shrink-0" />,
      text: 'Archive job',
      onClick: () => handleStatusChange(JobStatus.ARCHIVED)
    },
    {
      id: 'more-button-delete-job',
      icon: <DeleteIcon className="w-5 shrink-0" />,
      text: 'Delete',
      onClick: handleDeleteJob
    }
  ];

  const handleSidePanelBack = () => {
    if (isRootState) {
      handleSidePanelClose();
    } else {
      navigate(-1);
    }
  };

  const toggleIsMoreOpen = () => {
    setIsMoreOpen(!isMoreOpen);
  };

  const closeMoreOption = () => {
    setIsMoreOpen(false);
  };

  const handleAddInvoiceFromJob = () => {
    logAnalyticEvent(CleverTapEventsInvoices.webInvoicesCreateInvoice, { isFromJob: true });
    navigate(`/invoices/create?jobId=${job.id}`);
  };

  const handleInvoiceShare = (invoice: RelatedInvoiceForJob) => {
    if (invoice.status === InvoiceStatus.PENDING) {
      if (rootJobId) {
        navigate(`/jobs/${currentTeam.id}/${rootJobId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootCustomerId) {
        navigate(`/customers/${currentTeam.id}/${rootCustomerId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootDisputeId) {
        navigate(`/payments/disputes/${currentTeam.id}/${rootDisputeId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootReceiptId) {
        navigate(`/payments/receipts/${currentTeam.id}/${rootReceiptId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootInvoiceId) {
        navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootEstimateId) {
        navigate(`/estimates/${currentTeam.id}/${rootEstimateId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      }
    } else {
      if (rootJobId) {
        navigate(`/jobs/${currentTeam.id}/${rootJobId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootCustomerId) {
        navigate(`/customers/${currentTeam.id}/${rootCustomerId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootDisputeId) {
        navigate(`/payments/disputes/${currentTeam.id}/${rootDisputeId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootReceiptId) {
        navigate(`/payments/receipts/${currentTeam.id}/${rootReceiptId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootInvoiceId) {
        navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootEstimateId) {
        navigate(`/estimates/${currentTeam.id}/${rootEstimateId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      }
    }
  };

  const handleOpenRemoveInvoiceConfirmationModal = (invoiceId: string) => {
    setIsConfirmationModalOpen({ id: invoiceId, isOpen: true, type: ENTITY_TYPE.INVOICE });
  };

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen({ id: '', isOpen: false, type: null });
  };

  const handleRemoveInvoiceFromJob = () => {
    unlinkJobAndInvoiceMutation({
      jobId: job.id,
      invoiceId: isConfirmationModalOpen.id,
      teamId: currentTeam.id,
      sourceType: UnlinkSourceType.JOB
    });
    if (refetchEstimatesList) {
      refetchEstimatesList();
    }
  };

  const moreAnchorElem = () => (
    <div
      id="jobs-bottom-bar-more-button"
      className="flex shrink grow cursor-pointer items-center justify-center gap-2
      bg-secondary py-3.5 font-semibold text-primary hover:opacity-95">
      <div>More</div>
      <div className="path-stroke-current path">
        <CaretDownIcon className="rotate-180" />
      </div>
    </div>
  );

  const handleAddEstimateFromJob = () => {
    // logAnalyticEvent(CleverTapEventsInvoices.webInvoicesCreateEstimate, { isFromJob: true });
    if (
      currentTeam?.subscriptionStatus === SubscriptionStatus.SUBSCRIPTION_EXPIRED ||
      currentTeam?.subscriptionStatus === SubscriptionStatus.NOT_SUBSCRIBED
    ) {
      if (isOwner) navigate(routesPath.ESTIMATE_INTEGRATION);
      else navigate(routesPath.ESTIMATES);
      return;
    }
    dispatch(resetEstimateState());
    navigate(`/estimates/create?jobId=${job.id}`);
  };

  const handleEstimateClick = (estimateId: string) => {
    if (rootJobId) {
      navigate(`/jobs/${currentTeam.id}/${rootJobId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam.id}/${rootCustomerId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam.id}/${rootDisputeId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam.id}/${rootReceiptId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam.id}/${rootEstimateId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam.id}/${rootPage}/estimate/${estimateId}`);
    }
  };

  const handleOpenRemoveEstimateConfirmationModal = (estimateId: string) => {
    setIsConfirmationModalOpen({ id: estimateId, isOpen: true, type: ENTITY_TYPE.ESTIMATE });
  };

  const handleRemoveEstimateFromJob = async () => {
    try {
      await disconnectJobOrInvoiceFromEstimate({
        teamId: currentTeam.id,
        estimateId: isConfirmationModalOpen.id,
        entityType: ENTITY_TYPE.JOB,
        entityID: job.id,
        sourceType: UnlinkSourceType.JOB
      }).unwrap();
      handleCloseConfirmationModal();
      refetchJob(job.id);
      if (refetchEstimatesList) {
        refetchEstimatesList();
      }
    } catch (error) {
      // Handle error (e.g., show a notification)
    }
  };

  const hasRelatedEstimates = job?.estimates && job.estimates?.length > 0;
  const shoudlShowAddEstimateButton = (!job?.invoices || job?.invoices?.length === 0) && !hasRelatedEstimates;

  return (
    <div className="h-full w-96 max-w-sm">
      <If condition={isJobByIdFetching || isJobTimelineFetching}>
        <div className="flex h-full w-96 items-center justify-center">
          <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
        </div>
      </If>
      {job && !isJobByIdFetching && (
        <div className="flex h-full flex-col">
          <div className="flex items-center gap-2 p-4 pt-5">
            <div onClick={handleSidePanelBack} className="cursor-pointer">
              {isRootState ? (
                <NavCloseIcon id="job-details-panel-close" />
              ) : (
                <ArrowRightIcon className="rotate-180" id="job-details-panel-back-arrow" />
              )}
            </div>
            <div id="job-details-panel-head" className="text-17px font-semibold text-headingGray">
              {job.jobNumber}
            </div>
          </div>
          <div className="customNormalScroll flex grow flex-col overflow-y-auto overflow-x-hidden px-4">
            <div className="flex flex-col items-center justify-center py-6">
              <div className="max-w-[98%] break-words text-center text-2xl font-bold text-headingGray">{job.title}</div>
              <div
                className="mt-2 rounded px-2.5 py-1 leading-[18px]"
                style={{
                  backgroundColor: `${getJobStatusColor(job.status)}1A`,
                  color: getJobStatusColor(job.status)
                }}>
                {getJobStatusText(job.status)}
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <CustomerSection customer={job.customerCurrent} onCustomerClick={handleCustomerClick} />
              <div className="flex flex-col gap-y-2">
                <div className="text-sbase font-bold text-primaryText">Schedule</div>
                <div className="flex items-center gap-x-3 rounded-md border border-borderGray p-3 pr-4">
                  <CalendarIcon className="shrink-0" />
                  {job.isScheduled ? (
                    <>
                      <div className="flex w-full justify-between text-sbase font-semibold text-primaryBtn">
                        <div>
                          {DateUtils.getDateInFormat({
                            date: new Date(job.scheduledFrom),
                            dateFormat: 'MMM dd, yyyy'
                          })}
                        </div>
                        <div>
                          {`${DateUtils.formatTime(job.scheduledFrom)} - ${DateUtils.formatTime(job.scheduledTo)}`}
                        </div>
                      </div>
                      <EditIconBlue
                        className="h-[18px] w-[18px] shrink-0 cursor-pointer"
                        onClick={onEditScheduleClick}
                        role="presentation"
                      />
                    </>
                  ) : (
                    <div
                      className="cursor-pointer text-sbase font-semibold text-primaryBtn"
                      role="presentation"
                      onClick={onEditScheduleClick}>
                      Add a schedule
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-y-2">
                <div className="text-sbase font-bold text-primaryText">{`Team (${job.assignees?.length ?? 0})`}</div>
                <div
                  className={`flex items-center justify-between gap-x-3 rounded-md border border-borderGray p-3 pr-4
                            ${isSingleMemberTeam ? 'pointer-events-none' : 'cursor-pointer'}`}
                  role="presentation"
                  onClick={onEditAssigneesClick}>
                  {job.assignees?.length > 0 ? (
                    <>
                      <div className="text-sbase font-semibold text-primaryText">{getTeamMemberNames()}</div>
                      {(manageTeamPermissions.editExistingMember || manageTeamPermissions.viewExistingMember) &&
                        !isSingleMemberTeam && <EditIconBlue className="h-[18px] w-[18px] shrink-0" />}
                    </>
                  ) : (
                    <div
                      className="flex cursor-pointer items-center"
                      role="presentation"
                      onClick={onEditAssigneesClick}>
                      <CustomersTabIcon
                        className="path-stroke-current path-fill-current path mr-2 text-secondary"
                        width={20}
                        height={20}
                      />
                      <div className="text-sbase font-semibold text-primaryBtn">Assign team members</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-y-2">
                <div className="text-sbase font-bold text-primaryText">Status</div>
                <div
                  className="flex cursor-pointer items-center justify-between gap-x-3 rounded-md border 
                border-borderGray p-3 pr-4"
                  role="presentation"
                  onClick={onEditStatusClick}>
                  <div className="flex items-center gap-x-2">
                    <div
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: getJobStatusColor(job.status) }}
                    />
                    <div className="text-sbase font-semibold" style={{ color: getJobStatusColor(job.status) }}>
                      {getJobStatusText(job.status)}
                    </div>
                  </div>
                  <ChevronArrowRightReactIcon
                    id="job-detail-status-edit-btn"
                    className="shrink-0 cursor-pointer"
                    onClick={() => null}
                    fill={WebAppColors.Gray_1}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-y-2">
                <div className="text-sbase font-bold text-primaryText">Job description</div>
                <div className="rounded-md border border-borderGray p-3 pr-4">
                  {job.description ? (
                    <div className="flex items-start justify-between ">
                      <div
                        className="text-ellipsis whitespace-pre-wrap break-words text-sbase font-semibold 
                                  text-primaryText line-clamp-3">
                        {job.description}
                      </div>
                      <EditIconBlue
                        className="h-[18px] w-[18px] shrink-0 cursor-pointer"
                        onClick={onEditDescriptionClick}
                      />
                    </div>
                  ) : (
                    <div
                      className="flex cursor-pointer items-center"
                      role="presentation"
                      onClick={onEditDescriptionClick}>
                      <AddIconWoPadding className="mr-2 shrink-0" />
                      <div className="text-sbase font-semibold text-primaryBtn"> Add a description </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-y-2">
                <div className="text-sbase font-bold text-primaryText">Lead source</div>
                <div
                  className="flex cursor-pointer items-center justify-between gap-x-3 rounded-md border 
                border-borderGray p-3 pr-4"
                  role="presentation"
                  onClick={onEditLeadSourceClick}>
                  <div className="flex items-center gap-x-2">
                    <div className="text-sbase font-semibold capitalize text-primaryText">
                      {`${job.leadSourceType} ${job.leadSource ? `- ${job.leadSource}` : ''}`}
                    </div>
                  </div>
                  <ChevronArrowRightReactIcon
                    id="job-detail-status-edit-btn"
                    className="shrink-0"
                    fill={WebAppColors.Gray_1}
                  />
                </div>
              </div>
              {job.lineItems && (
                <LineItemsSection
                  lineItems={job.lineItems}
                  totalAmount={job.payableAmount}
                  discount={job.discount}
                  payableAmount={job.billableAmount}
                />
              )}
              <div className="flex flex-col gap-y-2">
                <div className="text-sbase font-bold text-primaryText">Invoice</div>
                {job.invoices?.length > 0 ? (
                  <>
                    {job.invoices.map(invoice => (
                      <div key={invoice.id} className="w-full">
                        <RelatedInvoiceCard
                          invoice={invoice as unknown as InvoiceDetails}
                          onInvoiceClick={handleInvoiceClick}
                          onRemoveInvoice={handleOpenRemoveInvoiceConfirmationModal}
                          onShareInvoice={handleInvoiceShare}
                        />
                      </div>
                    ))}
                  </>
                ) : (
                  <div
                    className="flex cursor-pointer items-center justify-center gap-x-3 rounded-md border 
                border-borderGray p-3 pr-4"
                    role="presentation"
                    onClick={handleAddInvoiceFromJob}>
                    <div className="flex items-center">
                      <AddIconWoPadding className="mr-2 shrink-0" />
                      <div className="text-sbase font-semibold text-primaryBtn">Add invoice</div>
                    </div>
                  </div>
                )}
              </div>

              {hasRelatedEstimates && (
                <div className="flex flex-col gap-y-2">
                  <div className="text-sbase font-bold text-primaryText">Estimate</div>
                  {job.estimates?.map(estimate => (
                    <RelatedEstimateCard
                      key={estimate.id}
                      estimate={estimate}
                      onEstimateClick={handleEstimateClick}
                      onRemoveEstimate={handleOpenRemoveEstimateConfirmationModal}
                    />
                  ))}
                </div>
              )}
              {shoudlShowAddEstimateButton && (
                <div className="flex flex-col gap-y-2">
                  <div className="text-sbase font-bold text-primaryText">Estimate</div>
                  <div
                    className="flex cursor-pointer items-center justify-center gap-x-3 rounded-md border 
                    border-borderGray p-3 pr-4"
                    role="presentation"
                    onClick={handleAddEstimateFromJob}>
                    <div className="flex items-center">
                      <AddIconWoPadding className="mr-2 shrink-0" />
                      <div className="text-sbase font-semibold text-primaryBtn">Add estimate</div>
                    </div>
                  </div>
                </div>
              )}
              <JobTimelineSection
                jobTimeline={jobTimeline}
                onShowMoreClick={onViewHistoryClick}
                onEditNoteClick={onEditNoteClick}
                onDeleteNoteClick={onDeleteNote}
                currentActionsPopoverId={currentNotesActionsPopoverId}
                setCurrentActionsPopoverId={setCurrentNotesActionsPopoverId}
              />
              <div className="flex flex-col gap-y-4 pb-4">
                <div className="text-sbase font-bold text-primaryText">Additional info</div>
                <div className="flex items-center justify-between">
                  <div className="text-sbase font-semibold text-accentText">Job number</div>
                  <div className="text-right text-sbase font-semibold text-tertiaryText">{job.jobNumber}</div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-sbase font-semibold text-accentText">Reference ID</div>
                  <div className="text-right text-sbase font-semibold text-tertiaryText">{job.referenceNo || '-'}</div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-sbase font-semibold text-accentText">Created on</div>
                  <div className="text-right text-sbase font-semibold text-tertiaryText">
                    {DateUtils.getDateInFormat({ date: new Date(job.createdAt), dateFormat: 'dd MMM yyyy' })}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-sbase font-semibold text-accentText">Created by</div>
                  <div className="text-right text-sbase font-semibold text-tertiaryText">{job.createdBy.name}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-auto flex justify-between gap-px justify-self-end">
            {bottomBar.map(item => (
              <div
                id={`job-side-panel-${item.btnText}-button`}
                onClick={() => {
                  item.onClickHandler();
                }}
                key={item.id + item.btnText}
                className={twMerge(
                  classNames(
                    `text-primary bg-secondary flex gap-2 py-3.5 grow
                       justify-center items-center font-semibold cursor-pointer hover:opacity-95`,
                    item.isDisabled ? 'bg-[#9BBADD] hover:opacity-100' : 'shrink items-center'
                  )
                )}>
                <div className={`${item.isDisabled && 'opacity-50'}`}>{item.icon}</div>
                <div className={`text-sbase ${item.isDisabled && 'opacity-50'}`}>{item.btnText}</div>
                {item.isDisabled && <WhiteInfoIcon />}
              </div>
            ))}
            <div
              className="flex shrink grow cursor-pointer items-center justify-center
                           gap-2 bg-secondary hover:opacity-95"
              onClick={toggleIsMoreOpen}>
              <CustomPopover
                show={isMoreOpen}
                anchorComponent={moreAnchorElem}
                onTogglePopover={toggleIsMoreOpen}
                onClosePopover={closeMoreOption}
                offset="-right-3 bottom-14"
                minWidth="min-w-[160px]"
                showArrow={true}
                arrowType={PopoverArrowTypes.BOTTOM}
                highlightAnchor={false}>
                <div></div>
                {<MoreMenu menuItems={moreMenuItems} />}
              </CustomPopover>
            </div>
          </div>
        </div>
      )}
      {isConfirmationModalOpen.isOpen && isConfirmationModalOpen.type === ENTITY_TYPE.INVOICE && (
        <GenericDeleteModal
          onPrimaryBtnClick={handleRemoveInvoiceFromJob}
          onSecondaryBtnClick={handleCloseConfirmationModal}
          isActionInProgress={isUnlinkInProgress}
          primaryBtnId="remove-invoice-confirm"
          primaryBtnLabel="Yes, remove"
          secondaryBtnId="remove-invoice-cancel"
          secondaryBtnLabel="No, cancel"
          showModal={true}
          title="Remove invoice"
          subTitle="Are you sure you want to remove invoice from job?"
        />
      )}
      {isConfirmationModalOpen.isOpen && isConfirmationModalOpen.type === ENTITY_TYPE.ESTIMATE && (
        <GenericDeleteModal
          onPrimaryBtnClick={handleRemoveEstimateFromJob}
          onSecondaryBtnClick={handleCloseConfirmationModal}
          isActionInProgress={isDisconnecting}
          primaryBtnId="remove-estimate-confirm"
          primaryBtnLabel="Yes, remove"
          secondaryBtnId="remove-estimate-cancel"
          secondaryBtnLabel="No, cancel"
          showModal={true}
          title="Remove Estimate"
          subTitle="Are you sure you want to remove this estimate from the job?"
        />
      )}
      {!isJobByIdFetching && isError && <GenericError handleErrorBack={handleSidePanelBack} />}
    </div>
  );
};

export default JobDetailsPanel;
